/**
 * Canvas renderer for CRYPTOBOOM. Pure-ish: reads from engine state `s` +
 * a preloaded mascot Image, writes only to the provided canvas 2D context.
 */

const priceFromY = (y, H) => 1 - y / H;

function drawBackground(ctx, s) {
  const { W, H } = s;
  ctx.fillStyle = "#0a0a18";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "rgba(46,117,182,0.12)";
  const off = s.scrollX % 28;
  for (let x = -off; x < W; x += 28) {
    for (let y = 0; y < H; y += 28) ctx.fillRect(x, y, 1, 1);
  }
}

function drawCandles(ctx, s) {
  const { W, H } = s;
  const candleW = 12;
  const gap = 4;
  const stepX = candleW + gap;
  const baseX = W - 100;
  const maxCandles = Math.min(s.candles.length, Math.floor(W / stepX) + 6);
  for (let i = 0; i < maxCandles; i++) {
    const c = s.candles[s.candles.length - 1 - i];
    if (!c) break;
    const x = baseX - i * stepX;
    if (x < -candleW) break;
    const yo = H - priceFromY(c.o, H) * H;
    const yc = H - priceFromY(c.c, H) * H;
    const yh = H - priceFromY(c.h, H) * H;
    const yl = H - priceFromY(c.l, H) * H;
    const up = c.c >= c.o;
    const col = up ? "rgba(46,204,113,0.55)" : "rgba(255,80,80,0.55)";
    const colBody = up ? "#2ecc71" : "#ff5252";
    ctx.strokeStyle = col;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + candleW / 2, yh);
    ctx.lineTo(x + candleW / 2, yl);
    ctx.stroke();
    ctx.fillStyle = colBody;
    const top = Math.min(yo, yc);
    const hBody = Math.max(2, Math.abs(yc - yo));
    ctx.globalAlpha = 0.65;
    ctx.fillRect(x, top, candleW, hBody);
    ctx.globalAlpha = 1;
  }
}

function drawPriceLine(ctx, s) {
  if (s.priceHistory.length < 2) return;
  const { W, H } = s;
  const first = s.priceHistory[0];
  const last = s.priceHistory[s.priceHistory.length - 1];
  const up = last.price >= first.price;
  const lineColor = up ? "#2ecc71" : "#ff5252";

  const fillGrad = ctx.createLinearGradient(0, 0, 0, H);
  fillGrad.addColorStop(0, up ? "rgba(46,204,113,0.22)" : "rgba(255,82,82,0.22)");
  fillGrad.addColorStop(1, "rgba(10,10,24,0)");
  ctx.fillStyle = fillGrad;
  ctx.beginPath();
  ctx.moveTo(s.priceHistory[0].x, H);
  for (const p of s.priceHistory) ctx.lineTo(p.x, H - priceFromY(p.price, H) * H);
  ctx.lineTo(s.priceHistory[s.priceHistory.length - 1].x, H);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = lineColor;
  ctx.shadowColor = up ? "rgba(46,204,113,0.65)" : "rgba(255,82,82,0.55)";
  ctx.shadowBlur = 12;
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < s.priceHistory.length; i++) {
    const p = s.priceHistory[i];
    const py = H - priceFromY(p.price, H) * H;
    if (i === 0) ctx.moveTo(p.x, py);
    else ctx.lineTo(p.x, py);
  }
  ctx.stroke();
  ctx.shadowBlur = 0;

  const cur = H - priceFromY(last.price, H) * H;
  ctx.strokeStyle = up ? "rgba(46,204,113,0.35)" : "rgba(255,82,82,0.35)";
  ctx.setLineDash([6, 6]);
  ctx.beginPath();
  ctx.moveTo(0, cur);
  ctx.lineTo(W, cur);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawPriceBadge(ctx, s) {
  const latest = s.priceHistory[s.priceHistory.length - 1];
  const initial = s.priceHistory[0];
  if (!latest || !initial) return;
  const startP = initial.price;
  const nowP = latest.price;
  const pct = startP > 0 ? ((nowP - startP) / startP) * 100 : 0;
  const basePrice = 0.0001234;
  const displayPrice = (basePrice * (0.6 + nowP * 0.8)).toFixed(7);
  const up = nowP >= startP;
  ctx.save();
  ctx.font = "bold 10px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(15,15,30,0.7)";
  ctx.fillRect(8, 8, 128, 38);
  ctx.strokeStyle = up ? "rgba(46,204,113,0.7)" : "rgba(255,82,82,0.7)";
  ctx.strokeRect(8, 8, 128, 38);
  ctx.fillStyle = "#8b95a9";
  ctx.fillText("$CBOOM", 14, 22);
  ctx.fillStyle = up ? "#2ecc71" : "#ff5252";
  ctx.fillText(`$${displayPrice}`, 14, 36);
  ctx.textAlign = "right";
  ctx.fillText(`${up ? "▲" : "▼"}${Math.abs(pct).toFixed(1)}%`, 132, 36);
  ctx.restore();
}

function drawObstacles(ctx, s) {
  for (const o of s.obstacles) {
    const drawBlocks = (blocks) => {
      for (const b of blocks) {
        if (!b.alive) continue;
        ctx.save();
        ctx.translate(o.x, b.y);
        if (b.destructible) {
          ctx.fillStyle = "rgba(46,204,113,0.15)";
          ctx.strokeStyle = "#2ecc71";
          ctx.shadowColor = "rgba(46,204,113,0.9)";
          ctx.shadowBlur = 10;
        } else {
          ctx.fillStyle = "rgba(160,170,180,0.18)";
          ctx.strokeStyle = "#8b95a9";
          ctx.shadowColor = "rgba(255,255,255,0.2)";
          ctx.shadowBlur = 4;
        }
        ctx.lineWidth = 2;
        const w = 44;
        ctx.fillRect(0, 0, w, b.h);
        ctx.strokeRect(0, 0, w, b.h);
        if (b.label) {
          ctx.shadowBlur = 0;
          ctx.fillStyle = "#F7931A";
          ctx.font = "bold 9px 'JetBrains Mono', monospace";
          ctx.textAlign = "center";
          ctx.fillText(b.label, w / 2, b.h / 2 + 3);
        }
        ctx.restore();
      }
    };
    drawBlocks(o.topBlocks);
    drawBlocks(o.bottomBlocks);
  }
}

function drawCoins(ctx, s) {
  for (const c of s.coins) {
    if (!c.alive) continue;
    ctx.save();
    ctx.translate(c.x, c.y);
    ctx.fillStyle = "#F7931A";
    ctx.shadowColor = "rgba(247,147,26,0.9)";
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#0f0f1e";
    ctx.font = "bold 9px 'JetBrains Mono', monospace";
    ctx.textAlign = "center";
    ctx.fillText("¢", 0, 3);
    ctx.restore();
  }
}

function drawProjectiles(ctx, s) {
  for (const p of s.projectiles) {
    ctx.save();
    ctx.fillStyle = "#2ecc71";
    ctx.shadowColor = "rgba(46,204,113,0.8)";
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawParticles(ctx, s) {
  for (const pt of s.particles) {
    ctx.save();
    ctx.globalAlpha = Math.max(0, pt.life / pt.maxLife);
    ctx.fillStyle = pt.color;
    const sz = pt.size || 3;
    ctx.fillRect(pt.x - sz / 2, pt.y - sz / 2, sz, sz);
    ctx.restore();
  }
}

function drawPlayer(ctx, s, mascotImg) {
  const { player } = s;
  const now = performance.now();
  const magnetActive = now < (s.magnetUntil || 0);
  const slowmoActive = now < (s.slowmoUntil || 0);

  ctx.save();
  ctx.translate(player.x, player.y);
  // halo
  const g = ctx.createRadialGradient(0, 0, player.r * 0.2, 0, 0, player.r * 2.2);
  g.addColorStop(0, "rgba(46,204,113,0.9)");
  g.addColorStop(0.6, "rgba(46,204,113,0.18)");
  g.addColorStop(1, "rgba(46,204,113,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, player.r * 2, 0, Math.PI * 2);
  ctx.fill();

  // Magnet aura
  if (magnetActive) {
    const pulse = 0.3 + 0.2 * Math.sin(now / 120);
    ctx.strokeStyle = `rgba(247,147,26,${pulse})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, 160, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Slowmo tint ring
  if (slowmoActive) {
    ctx.strokeStyle = "rgba(0,170,255,0.8)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, player.r * 1.6, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (mascotImg && mascotImg.complete && mascotImg.naturalWidth > 0) {
    const spriteSize = player.r * 2.6;
    const half = spriteSize / 2;
    ctx.drawImage(mascotImg, -half, -half, spriteSize, spriteSize);
    if (s.hitFlash > 0) {
      ctx.globalCompositeOperation = "source-atop";
      ctx.fillStyle = "rgba(255, 60, 60, 0.72)";
      ctx.fillRect(-half, -half, spriteSize, spriteSize);
      ctx.globalCompositeOperation = "source-over";
    }
  } else {
    ctx.fillStyle = s.hitFlash > 0 ? "#ff4d4d" : "#2ecc71";
    ctx.beginPath();
    ctx.arc(0, 0, player.r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Shield bubble (drawn on top)
  if (s.shieldActive) {
    const pulse = 0.6 + 0.3 * Math.sin(now / 150);
    ctx.strokeStyle = `rgba(0,170,255,${pulse})`;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, player.r * 1.9, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = `rgba(0,170,255,${pulse * 0.15})`;
    ctx.beginPath();
    ctx.arc(0, 0, player.r * 1.9, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawShockwave(ctx, s) {
  if (s.alive || !s.deadAt) return;
  const elapsed = performance.now() - s.deadAt;
  if (elapsed >= 600) return;
  const t = elapsed / 600;
  ctx.save();
  ctx.strokeStyle = `rgba(255, 160, 40, ${1 - t})`;
  ctx.lineWidth = 6 * (1 - t) + 1;
  ctx.beginPath();
  ctx.arc(s.player.x, s.player.y, 20 + t * 180, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = `rgba(46, 204, 113, ${0.8 * (1 - t)})`;
  ctx.lineWidth = 4 * (1 - t) + 1;
  ctx.beginPath();
  ctx.arc(s.player.x, s.player.y, 8 + t * 120, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

export function render(ctx, s, mascotImg) {
  drawBackground(ctx, s);
  drawCandles(ctx, s);
  drawPriceLine(ctx, s);
  drawPriceBadge(ctx, s);
  drawObstacles(ctx, s);
  drawCoins(ctx, s);
  drawProjectiles(ctx, s);
  drawParticles(ctx, s);
  if (s.alive) drawPlayer(ctx, s, mascotImg);
  else drawShockwave(ctx, s);
}
